import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Badge from '~/components/Badge'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Slider from '~/components/Slider'

const SliderPage: Component = () => {
  const [precio, setPrecio] = createSignal(40)
  const [volumen, setVolumen] = createSignal(60)
  const [cuota, setCuota] = createSignal(25)

  return (
    <>
      <Title>Slider - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Slider</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Control de valor continuo con riel recorrido, burbuja de valor al arrastrar, marcas sobre el riel y los colores y tamaños del tema.
          </p>
        </div>

        <div class="space-y-12">
          {/* Interacción */}
          <Card>
            <Card.Header
              title="Ajuste de valor"
              subtitle="Controlado con señal, burbuja de valor visible al arrastrar o pasar el cursor."
              icon={<Icon icon="material-symbols-light:tune" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-12 p-10 bg-surface-muted rounded-3xl border border-border border-dashed">
                <div class="space-y-6">
                  <h4 class="text-xs font-semibold text-foreground-subtle pl-3 border-l-4 border-primary">Precio (%)</h4>
                  <Slider min={0} max={100} value={precio()} onChange={setPrecio} showValue />
                  <p class="text-sm text-foreground-subtle">
                    Valor: <span class="font-mono text-foreground">{precio()}%</span>
                  </p>
                </div>
                <div class="space-y-6">
                  <h4 class="text-xs font-semibold text-foreground-subtle pl-3 border-l-4 border-success">Volumen</h4>
                  <Slider min={0} max={100} step={5} value={volumen()} onChange={setVolumen} color="success" showValue />
                  <p class="text-sm text-foreground-subtle">
                    Valor: <span class="font-mono text-foreground">{volumen()}</span>
                  </p>
                </div>
                <div class="space-y-6">
                  <h4 class="text-xs font-semibold text-foreground-subtle pl-3 border-l-4 border-info">Cuota inicial</h4>
                  <Slider min={0} max={100} step={5} value={cuota()} onChange={setCuota} color="info" showValue />
                  <p class="text-sm text-foreground-subtle">
                    Valor: <span class="font-mono text-foreground">{cuota()}%</span>
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Marcas */}
          <Card>
            <Card.Header
              title="Marcas sobre el riel"
              subtitle="Puntos de referencia con etiqueta, o una marca automática por step."
              icon={<Icon icon="material-symbols-light:flag-outline" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="space-y-14 p-10">
                <div class="space-y-4">
                  <h4 class="text-xs font-semibold text-foreground-subtle">Con etiquetas</h4>
                  <Slider
                    min={0}
                    max={100}
                    step={25}
                    defaultValue={50}
                    marks={[
                      { value: 0, label: 'Mínimo' },
                      { value: 25, label: 'Básico' },
                      { value: 50, label: 'Estándar' },
                      { value: 75, label: 'Premium' },
                      { value: 100, label: 'Máximo' }
                    ]}
                    showValue
                  />
                </div>
                <div class="space-y-4">
                  <h4 class="text-xs font-semibold text-foreground-subtle">Automáticas (marks={'{true}'})</h4>
                  <Slider min={0} max={50} step={10} defaultValue={20} marks color="success" />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Matriz de color */}
          <Card>
            <Card.Header
              title="Matriz de color"
              subtitle="El riel recorrido y el borde del pulgar usan el color sólido de cada variante."
              icon={<Icon icon="material-symbols-light:palette-outline" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-12 p-10">
                {(['primary', 'success', 'danger', 'warning', 'info', 'secondary'] as const).map(c => (
                  <div class="space-y-3">
                    <Slider min={0} max={100} defaultValue={60} color={c} showValue />
                    <Badge variant="soft" color={c}>
                      {c}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>

          {/* Tamaños */}
          <Card>
            <Card.Header
              title="Tamaños"
              subtitle="La escala completa xs → xl. El riel y el pulgar crecen en proporción."
              icon={<Icon icon="material-symbols-light:format-size" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="space-y-10 p-10">
                {(['xs', 'sm', 'md', 'lg', 'xl'] as const).map(size => (
                  <Slider min={0} max={100} defaultValue={60} size={size} color="primary" showValue />
                ))}
              </div>
            </Card.Body>
          </Card>

          {/* Estados */}
          <Card>
            <Card.Header
              title="Estados"
              subtitle="Deshabilitado y rango personalizado con paso fino."
              icon={<Icon icon="material-symbols-light:info-outline" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="space-y-10 p-10">
                <Slider min={0} max={100} defaultValue={70} disabled />
                <Slider min={1000} max={10000} step={250} defaultValue={4250} color="warning" showValue />
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones técnicas para el componente Slider."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">min / max</td>
                      <td class="px-6 py-4 italic text-xs">number</td>
                      <td class="px-6 py-4 font-mono">0 / 100</td>
                      <td class="px-6 py-4 italic">Límites del rango de valores.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">step</td>
                      <td class="px-6 py-4 italic text-xs">number</td>
                      <td class="px-6 py-4 font-mono">1</td>
                      <td class="px-6 py-4 italic">Incremento entre valores.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">value</td>
                      <td class="px-6 py-4 italic text-xs">number</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Valor controlado.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">defaultValue</td>
                      <td class="px-6 py-4 italic text-xs">number</td>
                      <td class="px-6 py-4 font-mono">min</td>
                      <td class="px-6 py-4 italic">Valor inicial en modo no controlado.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">size</td>
                      <td class="px-6 py-4 italic text-xs">xs | sm | md | lg | xl</td>
                      <td class="px-6 py-4 font-mono">'md'</td>
                      <td class="px-6 py-4 italic">Escala física del componente.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">color</td>
                      <td class="px-6 py-4 italic text-xs">primary | secondary | success | danger | warning | info | default</td>
                      <td class="px-6 py-4 font-mono">'primary'</td>
                      <td class="px-6 py-4 italic">Color del riel recorrido y borde del pulgar.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">disabled</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 italic">Deshabilita la interacción.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">showValue</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 italic">Burbuja con el valor al arrastrar o hacer hover.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">marks</td>
                      <td class="px-6 py-4 italic text-xs">SliderMarkType[] | boolean</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Marcas sobre el riel: array {`{value, label}`} o true para una por step.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">name</td>
                      <td class="px-6 py-4 italic text-xs">string</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Input oculto para integrarse con Form/useForm.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">onChange</td>
                      <td class="px-6 py-4 italic text-xs">(value: number) =&gt; void</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Se llama en cada cambio mientras se arrastra.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">onChangeEnd</td>
                      <td class="px-6 py-4 italic text-xs">(value: number) =&gt; void</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4 italic">Se llama una sola vez al soltar el pulgar.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default SliderPage
